// app/src/features/onboarding/useOnboardingWiring.js
import { useLayoutEffect, useRef } from "react";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { useDrawerHelp } from "../../hooks/useDrawerHelp.js";
import { useRailTourFlash } from "../../hooks/useNavRail.js";
import { useOnboardingTips } from "../../hooks/useOnboardingTips.js";
import { useSayerTour } from "../../hooks/useSayerTour.js";
import { resetAllDrawerHelp } from "../../onboarding/drawerHelp";
import { TIPS_HELP_TEXT } from "../../onboarding/tips.js";

/**
 * The onboarding wiring: the drawer help (first-open banners), the tour controller with its
 * 37-field argument object, the three forward refs the tour hands to earlier- and later-registered
 * code, the first-contact tips, the tips' help text and the rail flash. Called where `AppInner`
 * called `useSayerTour` (after `useChatMessages` — it needs `submit` and `pushSayerMsg` — and
 * before `useChatJournal`); the cells register here in their old relative order (the PR's
 * hook-order note): the four tour-only refs, the drawer help, the tour, the two ref writes, the
 * tips, the rail flash, `tourRef`.
 *
 * The three forward refs are written in layout effects (every commit, no array) rather than at
 * render, as `AppInner` wrote them: each is read only after commit — `setTourExamplesRef` by
 * `resetForecast` (a handler path), `resumeTourRef` by the boot restore (a passive effect),
 * `tourRef` by `handleChatMessage` (a handler). `setTourExamplesRef` and `resumeTourRef` are
 * `AppInner`'s objects (the submit wiring and the boot wiring take them before this point) and
 * arrive as inputs; `tourRef` is created here, its only consumer (`useChatWiring`) is after.
 *
 * `drawerHelpState` / `setDrawerHelpState` (a raw `useState` setter) and `resetTips` are returned
 * under their names: the Settings and Account contexts read them. `drawerSeenPendingRef`,
 * `tourSayCount`, `resumeTour`, `endTour` and `answerTourAside` are internal (the tour, the rail
 * flash and the two ref writes read them; nothing in `AppInner` does).
 *
 * Not here: the eight tour refs earlier wirings take as arguments (`lastTourPendingIdRef`,
 * `tourExampleRunIdRef`, `exampleRevealTimerRef`, `postTourNudgeTimerRef`, `postTourTipRef`,
 * `postTourTipRunRef`, `resumeTourRef`, `setTourExamplesRef` — `AppInner`'s, passed in), the
 * tour's module globals and their getter thunks (rows 4/5/7/8 read them too), `pendingAbortsRef`
 * / `contractNotePendingRef` / `forecastNextRef` (submit's and chat's as much as the tour's). The
 * active forecast is read through `useActiveForecast()`.
 *
 * @param {object} deps  the bindings `AppInner` declares before this point (see the body)
 */
export function useOnboardingWiring({
  submit,
  pushSayerMsg,
  resetForecast,
  setInput,
  setChatOpen,
  autoZoom,
  openReport,
  closeReportRef,
  forecastAbortRef,
  pendingAbortsRef,
  setChatThinking,
  setLoading,
  setForecastingPhase,
  contractNotePendingRef,
  lastTourPendingIdRef,
  postTourNudgeTimerRef,
  postTourTipRef,
  postTourTipRunRef,
  forecastNextRef,
  exampleRevealTimerRef,
  tourExampleRunIdRef,
  branches,
  hov,
  logOpen,
  contractOpen,
  chatOpen,
  showReport,
  tsDrawerOpen,
  proOpen,
  accountOpen,
  isPhone,
  forecastsLoaded,
  getAuthEnabled,
  setTourFixture,
  setTourExamplesRef,
  resumeTourRef,
}) {
  const active = useActiveForecast();
  // Set true ONLY when the tour prompts the user's first forecast (the
  // forecastNextRef path). ninetyNotePendingRef → "A forecast may take a minute
  // to generate." (shown ~2s after the preprocess panel is dismissed);
  // contractNotePendingRef → "Next, a forecast contract will be created…" (shown
  // in submit, between the question and the generating status). Non-tour
  // forecasts never set them, so both notes are tour-only.
  const ninetyNotePendingRef = useRef(false);
  // Holds the user's would-be forecast question while we ask, mid-tour, whether
  // they want to end the tour and run it. Null when no such prompt is pending.
  const tourEndConfirmRef = useRef(null);
  // One-shot: nudge to close a drawer left open >10s during the tabs step.
  const closeHintShownRef = useRef(false);
  // Throttle for the "click the pie chart itself" nudge fired when the user clicks
  // a timeline tour-spotlight bulb instead of the pie it points at (avoids chat spam
  // on repeated clicks). Stores the last nudge's timestamp (ms).
  const pieNudgeAtRef = useRef(0);
  const { drawerHelpState, setDrawerHelpState, drawerSeenPendingRef } = useDrawerHelp({
    contractOpen,
    logOpen,
    proOpen,
    accountOpen,
  });

  // Must come after useChatMessages because it needs `submit` and `pushSayerMsg`.
  const {
    tourActive,
    tourWaitKey,
    tourExamples,
    tourSpotlight,
    tourSayCount,
    suppressGuideNarration,
    enterFlash,
    bugFlash,
    endTourBtn,
    postTourActive,
    setTourExamples,
    resumeTour,
    skipTour,
    endTour,
    signalTourAction,
    answerTourAside,
    handleEndTourBtn,
    endPostTour,
  } = useSayerTour({
    submit,
    pushSayerMsg,
    resetForecast,
    resetAllDrawerHelp,
    setDrawerHelpState,
    drawerSeenPendingRef,
    setInput,
    setChatOpen,
    autoZoom,
    openReport,
    closeReportRef,
    forecastAbortRef,
    pendingAbortsRef,
    setChatThinking,
    setLoading,
    setForecastingPhase,
    contractNotePendingRef,
    lastTourPendingIdRef,
    postTourNudgeTimerRef,
    postTourTipRef,
    postTourTipRunRef,
    ninetyNotePendingRef,
    forecastNextRef,
    exampleRevealTimerRef,
    tourExampleRunIdRef,
    tourEndConfirmRef,
    closeHintShownRef,
    active,
    branches,
    hov,
    logOpen,
    contractOpen,
    showReport,
    tsDrawerOpen,
    hydrationDone: forecastsLoaded,
    getAuthEnabled,
    setTourFixture,
  });
  // resetForecast (features/submit/useSubmitWiring.js, called before this wiring) clears the tour
  // examples at CLEAR time through this ref: the setter is declared here, after the submit
  // wiring, as at BASE. Kept current after every commit; resetForecast runs only from handlers.
  useLayoutEffect(() => {
    setTourExamplesRef.current = setTourExamples;
  });
  // The boot restore (commands/bootForecasts.ts, through useBootWiring's passive effect) calls
  // resumeTour after its loadForecasts await — after this layout effect has run.
  useLayoutEffect(() => {
    resumeTourRef.current = resumeTour;
  });
  // First-contact tips are how a new user meets Sayer (the guided tour is retired). Not on the
  // phone layout, which is read-only and has none of the controls they point at.
  const { tip, dismissTip, turnOffTips, resetTips } = useOnboardingTips({ enabled: !isPhone });
  const howSayerWorks = TIPS_HELP_TEXT;
  // In the rail layout the tour points by flashing a rail icon, not by edge-tab lightbulbs.
  const railTourFlash = useRailTourFlash({
    enabled: tourActive,
    tourSpotlight,
    tourWaitKey,
    logOpen,
    chatOpen,
    contractOpen,
    tourSayCount,
  });

  // tourRef is populated after each commit with the tour controller's handles, so the chat's
  // handleChatMessage (useChatIntent, which takes the ref object) reads the latest tour state at
  // call time without depending on it.
  const tourRef = useRef({});
  useLayoutEffect(() => {
    tourRef.current = {
      tourActive,
      tourWaitKey,
      skipTour,
      endTour,
      signalTourAction,
      answerTourAside,
    };
  });

  return {
    tourActive,
    tourWaitKey,
    tourExamples,
    tourSpotlight,
    suppressGuideNarration,
    enterFlash,
    bugFlash,
    endTourBtn,
    postTourActive,
    setTourExamples,
    skipTour,
    signalTourAction,
    handleEndTourBtn,
    endPostTour,
    tourRef,
    ninetyNotePendingRef,
    tourEndConfirmRef,
    closeHintShownRef,
    pieNudgeAtRef,
    drawerHelpState,
    setDrawerHelpState,
    tip,
    dismissTip,
    turnOffTips,
    resetTips,
    howSayerWorks,
    railTourFlash,
  };
}
